import React, { createContext, useContext, useMemo } from "react";
import { GetUserResponse, GetTransactionsPaginatedResponse, BrandChallenge } from "@devour/client";

/**
 * CockpitDataContext - Centralized data management for CockpitV3
 *
 * POC STATUS:
 * - Using existing hooks and passing data via context
 * - Simple data structure for MVP
 * - Manual refresh via parent component
 *
 * FUTURE STATE:
 * - WebSocket for real-time updates
 * - Optimistic updates for user actions
 * - Delta updates instead of full refetch
 * - GraphQL subscriptions for specific data changes
 * - Server-side aggregation endpoints
 */

// Me Column Data
export interface MeColumnData {
    userProfile?: GetUserResponse;
    transactions?: GetTransactionsPaginatedResponse;
    overwolfSummary?: any; // GetUserSummaryResponse
    leaderboardRank?: number;
    isLoading: boolean;
    error?: Error;
}

// Do Column Data
export interface DoColumnData {
    challenges?: BrandChallenge[];
    quests?: any[]; // Quest data when available
    missions?: any[]; // Mission data when available
    currentGame?: any; // Current game data
    isLoading: boolean;
    error?: Error;
}

// Us Column Data
export interface UsColumnData {
    guild?: any; // Guild data
    guildMembers?: any[]; // Guild members
    leaderboard?: any[]; // Leaderboard data
    socialFeed?: any[]; // Social activity feed
    isLoading: boolean;
    error?: Error;
}

// Context value type
export interface CockpitDataContextValue {
    meData: MeColumnData;
    doData: DoColumnData;
    usData: UsColumnData;
    refetchAll: () => void;
    refetchMe: () => void;
    refetchDo: () => void;
    refetchUs: () => void;
}

// Create context with default values
const CockpitDataContext = createContext<CockpitDataContextValue>({
    meData: { isLoading: false },
    doData: { isLoading: false },
    usData: { isLoading: false },
    refetchAll: () => {},
    refetchMe: () => {},
    refetchDo: () => {},
    refetchUs: () => {},
});

// Provider props
interface CockpitDataProviderProps {
    children: React.ReactNode;
    meData: MeColumnData;
    doData: DoColumnData;
    usData: UsColumnData;
    refetchAll: () => void;
    refetchMe: () => void;
    refetchDo: () => void;
    refetchUs: () => void;
}

/**
 * CockpitDataProvider - Provides centralized data to all cockpit columns
 */
export const CockpitDataProvider: React.FC<CockpitDataProviderProps> = ({
    children,
    meData,
    doData,
    usData,
    refetchAll,
    refetchMe,
    refetchDo,
    refetchUs,
}) => {
    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        meData,
        doData,
        usData,
        refetchAll,
        refetchMe,
        refetchDo,
        refetchUs,
    }), [meData, doData, usData, refetchAll, refetchMe, refetchDo, refetchUs]);

    return (
        <CockpitDataContext.Provider value={value}>
            {children}
        </CockpitDataContext.Provider>
    );
};

/**
 * Hook to access cockpit data from any component
 */
export const useCockpitData = () => {
    const context = useContext(CockpitDataContext);
    if (!context) {
        throw new Error("useCockpitData must be used within CockpitDataProvider");
    }
    return context;
};

/**
 * Hook to access only Me column data
 */
export const useMeColumnData = () => {
    const { meData, refetchMe } = useCockpitData();
    return { ...meData, refetch: refetchMe };
};

/**
 * Hook to access only Do column data
 */
export const useDoColumnData = () => {
    const { doData, refetchDo } = useCockpitData();
    return { ...doData, refetch: refetchDo };
};

/**
 * Hook to access only Us column data
 */
export const useUsColumnData = () => {
    const { usData, refetchUs } = useCockpitData();
    return { ...usData, refetch: refetchUs };
};